/**
 * Countdown Timer Class - Version 2
 * Handles countdown functionality for timer sections
 * Fixed null target date handling
 */

// Prevent duplicate execution - wrap everything to prevent conflicts
(function() {
'use strict';

// Check if already loaded
if (window.timerV2Loaded) {
  console.warn('Timer V2 already loaded, skipping duplicate initialization...');
  return;
}
window.timerV2Loaded = true;

console.log('=== SECTION TIMER V2 JS LOADED ===');

class CountdownTimer {
  constructor(element, targetDate) {
    this.element = element;
    this.targetDate = targetDate;
    
    // Verify element exists
    if (!element) {
      console.error('CountdownTimer: Element is null or undefined');
      return;
    }
    
    this.hoursElement = element.querySelector('[data-hours]');
    this.minutesElement = element.querySelector('[data-minutes]');
    this.secondsElement = element.querySelector('[data-seconds]');
    
    // Check if all required elements exist
    if (!this.hoursElement || !this.minutesElement || !this.secondsElement) {
      console.error('CountdownTimer: Required timer elements not found', {
        hours: !!this.hoursElement,
        minutes: !!this.minutesElement,
        seconds: !!this.secondsElement,
        element: element,
        elementHTML: element ? element.outerHTML.substring(0, 200) : 'null'
      });
      return; // Don't initialize if elements are missing
    }
    
    // Get reset duration from data attributes
    this.resetHours = parseInt(this.element.getAttribute('data-reset-hours')) || 1;
    this.resetMinutes = parseInt(this.element.getAttribute('data-reset-minutes')) || 0;
    
    // Storage key for persistence across pages and refreshes
    this.storageKey = 'announcement-timer-target-date';
    
    // Initialize interval as null
    this.interval = null;
    
    this.init();
  }

  init() {
    console.log('CountdownTimer V2 init - targetDate:', this.targetDate);
    
    // Check localStorage for a persisted target date
    const storedTargetDate = this.getStoredTargetDate();
    const now = new Date().getTime();
    
    // Priority: 
    // 1. If DOM has a target date set (from Shopify settings), use it (highest priority)
    // 2. Otherwise, use stored date from localStorage if valid
    // 3. Otherwise, start with reset duration
    let finalTargetDate = null;
    
    if (this.targetDate && !isNaN(this.targetDate.getTime()) && this.targetDate.getTime() > now) {
      // DOM target date is valid and in the future - use it (highest priority)
      // This allows users to set a specific target date in Shopify settings
      console.log('Using target date from DOM (Shopify settings):', this.targetDate);
      finalTargetDate = this.targetDate;
    } else if (storedTargetDate && !isNaN(storedTargetDate.getTime()) && storedTargetDate.getTime() > now) {
      // No DOM date, but stored date is valid - use it for persistence
      console.log('Using stored target date from localStorage:', storedTargetDate);
      finalTargetDate = storedTargetDate;
    }
    
    // If we have a valid final target date, use it
    if (finalTargetDate && !isNaN(finalTargetDate.getTime()) && finalTargetDate.getTime() > now) {
      this.targetDate = finalTargetDate;
      this.storeTargetDate(finalTargetDate);
    } else {
      // No valid target date - start with reset duration immediately
      console.log('No valid target date found, starting with reset duration');
      this.startWithResetDuration();
      return; // startWithResetDuration handles everything
    }
    
    // Clear any existing interval
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    
    // Update immediately to show the countdown right away
    this.updateTimer();
    
    // Set up interval
    this.interval = setInterval(() => {
      this.updateTimer();
    }, 1000);
    
    console.log('Timer started with target date:', this.targetDate);
    console.log('Time until expiry:', Math.floor((this.targetDate.getTime() - now) / 1000), 'seconds');
    console.log('Timer interval set:', !!this.interval);
    
    // Force an immediate update after a brief delay to ensure display updates
    setTimeout(() => {
      if (this.interval) {
        this.updateTimer();
        console.log('Forced immediate timer update');
      }
    }, 50);
  }
  
  getStoredTargetDate() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const date = new Date(stored);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return null;
  }
  
  storeTargetDate(targetDate) {
    try {
      localStorage.setItem(this.storageKey, targetDate.toISOString());
      console.log('Stored target date in localStorage:', targetDate.toISOString());
    } catch (error) {
      console.error('Error storing to localStorage:', error);
    }
  }

  startWithResetDuration() {
    console.log('startWithResetDuration called');
    console.log('Reset hours:', this.resetHours);
    console.log('Reset minutes:', this.resetMinutes);
    
    // Calculate target date from current time + reset duration
    const now = new Date();
    const totalMinutes = (this.resetHours * 60) + this.resetMinutes;
    this.targetDate = new Date(now.getTime() + (totalMinutes * 60 * 1000));
    
    // Verify target date is valid and in the future
    const nowTime = now.getTime();
    const targetTime = this.targetDate.getTime();
    
    if (isNaN(targetTime) || targetTime <= nowTime) {
      console.error('Invalid target date calculated:', this.targetDate);
      // Fallback: set to 1 hour from now
      this.targetDate = new Date(nowTime + (60 * 60 * 1000));
    }
    
    // Update the data attribute
    this.element.setAttribute('data-target-date', this.targetDate.toISOString());
    
    // Store in localStorage for persistence across pages
    this.storeTargetDate(this.targetDate);
    
    console.log('Starting timer with duration:', this.resetHours + 'h ' + this.resetMinutes + 'm');
    console.log('Target date set to:', this.targetDate);
    console.log('Current time:', now);
    console.log('Time difference (ms):', this.targetDate.getTime() - now.getTime());
    console.log('Time until expiry:', Math.floor((this.targetDate.getTime() - now.getTime()) / 1000), 'seconds');
    
    // Clear any existing interval
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    
    // Update immediately to show the countdown
    this.updateTimer();
    
    // Set up interval
    this.interval = setInterval(() => {
      this.updateTimer();
    }, 1000);
    
    console.log('Timer interval set up:', !!this.interval);
  }

  updateTimer() {
    // Safety check - ensure element is still in DOM
    if (!this.element || !document.body.contains(this.element)) {
      console.log('Timer element removed from DOM, cleaning up...');
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      return;
    }
    
    // Safety check - ensure timer elements still exist
    if (!this.hoursElement || !this.minutesElement || !this.secondsElement) {
      // Try to re-query elements in case they were re-rendered
      this.hoursElement = this.element.querySelector('[data-hours]');
      this.minutesElement = this.element.querySelector('[data-minutes]');
      this.secondsElement = this.element.querySelector('[data-seconds]');
      
      if (!this.hoursElement || !this.minutesElement || !this.secondsElement) {
        console.error('Timer elements not found, cannot update:', {
          hasHours: !!this.hoursElement,
          hasMinutes: !!this.minutesElement,
          hasSeconds: !!this.secondsElement,
          element: this.element
        });
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
        return;
      }
    }
    
    // Safety check - don't update if no valid target date
    if (!this.targetDate || isNaN(this.targetDate.getTime())) {
      console.log('updateTimer called but no valid target date, attempting to get stored date...');
      // Try to get stored date
      const storedDate = this.getStoredTargetDate();
      if (storedDate && storedDate.getTime() > new Date().getTime()) {
        console.log('Using stored date in updateTimer');
        this.targetDate = storedDate;
      } else {
        // If no valid stored date, reset
        console.log('No valid stored date, starting with reset duration');
        this.startWithResetDuration();
        return;
      }
    }

    const now = new Date().getTime();
    const target = this.targetDate.getTime();
    
    // Validate dates
    if (isNaN(now) || isNaN(target)) {
      console.error('Invalid date values:', { now, target, targetDate: this.targetDate });
      return;
    }
    
    const difference = target - now;

    if (difference > 0) {
      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      // Update display (double-check elements exist before updating)
      if (this.hoursElement && document.body.contains(this.hoursElement)) {
        this.hoursElement.textContent = this.formatNumber(hours);
      }
      
      if (this.minutesElement && document.body.contains(this.minutesElement)) {
        this.minutesElement.textContent = this.formatNumber(minutes);
      }
      
      if (this.secondsElement && document.body.contains(this.secondsElement)) {
        this.secondsElement.textContent = this.formatNumber(seconds);
      }
      
      // Safety check: if all values are zero but difference is positive, something is wrong
      if (hours === 0 && minutes === 0 && seconds === 0 && difference > 1000) {
        console.warn('Timer showing zeros but time remaining - forcing reset. Difference:', difference, 'ms');
        this.startWithResetDuration();
        return;
      }
      
      // Additional safety: if we have time remaining but calculated values are wrong, fix it
      if (difference > 0 && (hours < 0 || minutes < 0 || seconds < 0)) {
        console.error('Timer calculation error - negative values detected. Forcing reset.');
        this.startWithResetDuration();
        return;
      }
    } else if (difference <= 0) {
      // Timer has expired - reset to specified duration
      console.log('Timer expired, resetting...');
      this.resetTimer();
    } else {
      console.error('Unexpected difference value:', difference);
    }
  }

  resetTimer() {
    // Calculate new target date using stored reset values
    const now = new Date();
    const totalMinutes = (this.resetHours * 60) + this.resetMinutes;
    const newTargetDate = new Date(now.getTime() + (totalMinutes * 60 * 1000));
    
    // Update the target date
    this.targetDate = newTargetDate;
    
    // Update the data attribute for persistence
    this.element.setAttribute('data-target-date', newTargetDate.toISOString());
    
    // Store in localStorage for persistence across pages and refreshes
    this.storeTargetDate(newTargetDate);
    
    console.log('Timer reset to:', this.resetHours + 'h ' + this.resetMinutes + 'm');
    console.log('New target date:', newTargetDate);
    
    // Dispatch custom event for timer reset
    this.element.dispatchEvent(new CustomEvent('timerReset', {
      detail: { 
        newTargetDate: newTargetDate,
        resetHours: this.resetHours,
        resetMinutes: this.resetMinutes
      }
    }));
  }

  formatNumber(num) {
    return num.toString().padStart(2, '0');
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    // Clear references
    this.element = null;
    this.hoursElement = null;
    this.minutesElement = null;
    this.secondsElement = null;
  }
}

// Prevent multiple initializations per element
const activeTimers = new Map();

// Clean up old timer instances that reference removed DOM elements
function cleanupStaleTimers() {
  const staleElements = [];
  activeTimers.forEach((timer, element) => {
    // Check if element still exists in DOM
    if (!document.body.contains(element)) {
      console.log('Cleaning up stale timer instance for removed element');
      staleElements.push(element);
      if (timer && timer.destroy) {
        timer.destroy();
      }
    } else {
      // Also check if timer elements still exist
      const hoursEl = element.querySelector('[data-hours]');
      const minutesEl = element.querySelector('[data-minutes]');
      const secondsEl = element.querySelector('[data-seconds]');
      if (!hoursEl || !minutesEl || !secondsEl) {
        console.log('Cleaning up timer instance - timer elements missing');
        staleElements.push(element);
        if (timer && timer.destroy) {
          timer.destroy();
        }
      }
    }
  });
  
  // Remove stale entries
  staleElements.forEach(element => {
    activeTimers.delete(element);
    // Remove data attribute too
    if (element && element.removeAttribute) {
      element.removeAttribute('data-timer-initialized');
    }
  });
}

// Initialize all timer sections on page load
function initializeTimers() {
  console.log('=== Timer V2: Looking for timer elements ===');
  
  // Clean up any stale timer instances first
  cleanupStaleTimers();
  
  // Check localStorage first for a shared target date across all timers
  const storageKey = 'announcement-timer-target-date';
  let sharedTargetDate = null;
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const date = new Date(stored);
      const now = new Date().getTime();
      if (!isNaN(date.getTime()) && date.getTime() > now) {
        sharedTargetDate = date;
        console.log('Found valid stored target date:', sharedTargetDate);
      } else {
        console.log('Stored target date is invalid or expired:', date);
      }
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
  }
  
  // Handle timer-section__countdown elements
  const timerSections = document.querySelectorAll(".timer-section__countdown, .cart-timer-section__countdown, .announcement-timer-section__countdown");
  console.log('=== Timer Initialization Check ===');
  console.log('Found timer sections:', timerSections.length);
  console.log('Page URL:', window.location.href);
  console.log('Document ready state:', document.readyState);
  
  // Also check for timer elements by their data attributes
  const timerByDataAttr = document.querySelectorAll('[data-reset-hours]');
  console.log('Elements with data-reset-hours attribute:', timerByDataAttr.length);
  
  if (timerSections.length === 0) {
    console.warn('⚠️ No timer elements found on this page');
    console.log('Checking if announcement section exists...');
    const announcementSection = document.querySelector('#announcement, .black-friday-acccounment-container, .announcement-bar');
    console.log('Announcement section found:', !!announcementSection);
    
    // Check if timer is disabled in settings
    const timerEnabled = document.querySelector('[data-timer-initialized], .announcement-timer-section__countdown, [data-reset-hours]');
    console.log('Timer elements exist (any):', !!timerEnabled);
    
    // Don't return early - let the retry mechanism handle it
    // This allows for late-loading elements
  }
  
  timerSections.forEach((timerElement, index) => {
    // Check if element exists in DOM
    if (!document.body.contains(timerElement)) {
      console.log(`Timer ${index + 1}: Element not in DOM, skipping...`);
      return;
    }
    
    // Skip if currently being initialized (prevents race conditions)
    if (timerElement.hasAttribute('data-timer-initializing')) {
      console.log(`Timer ${index + 1}: Currently initializing, skipping...`);
      return;
    }
    
    // Skip if already initialized (check both Map and data attribute)
    if (activeTimers.has(timerElement) || timerElement.hasAttribute('data-timer-initialized')) {
      // Verify the timer instance is still valid
      const existingTimer = activeTimers.get(timerElement);
      if (existingTimer && existingTimer.interval) {
        console.log(`Timer ${index + 1}: Already initialized and active, skipping...`);
        return;
      } else {
        // Timer was marked as initialized but isn't active - clean it up
        console.log(`Timer ${index + 1}: Cleanup needed - marked as initialized but not active`);
        activeTimers.delete(timerElement);
        timerElement.removeAttribute('data-timer-initialized');
      }
    }
    
    // Check if timer elements exist
    const hoursEl = timerElement.querySelector('[data-hours]');
    const minutesEl = timerElement.querySelector('[data-minutes]');
    const secondsEl = timerElement.querySelector('[data-seconds]');
    if (!hoursEl || !minutesEl || !secondsEl) {
      console.log(`Timer ${index + 1}: Required timer elements not found, skipping...`);
      return;
    }
    
    console.log(`Timer ${index + 1}:`, timerElement);
    const targetDateString = timerElement.getAttribute("data-target-date");
    let targetDate = null;
    
    // Priority:
    // 1. DOM target date (from Shopify settings) - highest priority so users can set specific dates
    // 2. Stored date from localStorage - for persistence when no DOM date is set
    // 3. Reset duration - when neither exists
    if (targetDateString) {
      // DOM date is set - use it (highest priority)
      try {
        targetDate = new Date(targetDateString);
        const now = new Date().getTime();
        if (isNaN(targetDate.getTime())) {
          targetDate = null;
          console.log(`Timer ${index + 1}: Invalid DOM date format, will use stored date or reset`);
        } else if (targetDate.getTime() <= now) {
          targetDate = null;
          console.log(`Timer ${index + 1}: DOM target date is in the past, will use stored date or reset`);
        } else {
          console.log(`Timer ${index + 1}: Using target date from DOM (Shopify settings):`, targetDate);
        }
      } catch (error) {
        console.error("Error parsing target date:", error);
        targetDate = null;
      }
    }
    
    // If no valid DOM date, fall back to stored date
    if (!targetDate && sharedTargetDate) {
      targetDate = sharedTargetDate;
      console.log(`Timer ${index + 1}: No DOM date, using stored target date from localStorage:`, targetDate);
    } else if (!targetDate) {
      console.log(`Timer ${index + 1}: No target date found, will use reset duration`);
    }
    
    // Initialize timer (will use reset duration if no valid target date)
    // CountdownTimer will handle localStorage persistence internally
    try {
      // Double-check element still exists and isn't initialized (race condition protection)
      if (!document.body.contains(timerElement) || timerElement.hasAttribute('data-timer-initialized')) {
        console.log(`Timer ${index + 1}: Skipping - element removed or already initialized`);
        return;
      }
      
      // Mark as initializing to prevent race conditions
      timerElement.setAttribute('data-timer-initializing', 'true');
      
      let timer = null;
      try {
        timer = new CountdownTimer(timerElement, targetDate);
      } catch (error) {
        console.error(`Timer ${index + 1}: Error creating timer:`, error);
        timerElement.removeAttribute('data-timer-initializing');
        return;
      }
      
      if (timer) {
        activeTimers.set(timerElement, timer);
        // Mark element as initialized to prevent double initialization
        timerElement.setAttribute('data-timer-initialized', 'true');
        timerElement.removeAttribute('data-timer-initializing');
        
        // Check if timer has an active interval
        if (timer.interval) {
          console.log(`Timer ${index + 1}: Initialized successfully with active interval`);
        } else {
          console.warn(`Timer ${index + 1}: Initialized but no interval found - forcing reset`);
          // Force initialization with reset duration
          if (typeof timer.startWithResetDuration === 'function') {
            timer.startWithResetDuration();
          }
        }
        
        // Immediate check: Verify timer started properly
        setTimeout(() => {
          const hoursEl = timerElement.querySelector('[data-hours]');
          const minutesEl = timerElement.querySelector('[data-minutes]');
          const secondsEl = timerElement.querySelector('[data-seconds]');
          
          if (hoursEl && minutesEl && secondsEl) {
            const hours = parseInt(hoursEl.textContent) || 0;
            const minutes = parseInt(minutesEl.textContent) || 0;
            const seconds = parseInt(secondsEl.textContent) || 0;
            
            console.log(`Timer ${index + 1}: Current display after 200ms - ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
            console.log(`Timer ${index + 1}: Has interval:`, !!timer.interval);
            console.log(`Timer ${index + 1}: Target date:`, timer.targetDate);
            
            // If all zeros, force reset regardless
            if (hours === 0 && minutes === 0 && seconds === 0) {
              console.warn(`Timer ${index + 1}: Still showing zeros after initialization - forcing fresh start`);
              if (typeof timer.startWithResetDuration === 'function') {
                timer.startWithResetDuration();
              }
            }
          } else {
            console.error(`Timer ${index + 1}: Timer elements not found after initialization`);
          }
        }, 200);
        
        // Second check after 1 second
        setTimeout(() => {
          const hoursEl = timerElement.querySelector('[data-hours]');
          const minutesEl = timerElement.querySelector('[data-minutes]');
          const secondsEl = timerElement.querySelector('[data-seconds]');
          
          if (hoursEl && minutesEl && secondsEl) {
            const hours = parseInt(hoursEl.textContent) || 0;
            const minutes = parseInt(minutesEl.textContent) || 0;
            const seconds = parseInt(secondsEl.textContent) || 0;
            
            if (hours === 0 && minutes === 0 && seconds === 0 && timer.targetDate && timer.targetDate.getTime() > new Date().getTime()) {
              console.error(`Timer ${index + 1}: Still showing zeros after 1 second - this indicates a critical issue`);
              // Force reset one more time
              if (typeof timer.startWithResetDuration === 'function') {
                timer.startWithResetDuration();
              }
            }
          }
        }, 1000);
      } else {
        timerElement.removeAttribute('data-timer-initializing');
        console.error(`Timer ${index + 1}: Failed to create timer object - timer is null`);
      }
    } catch (error) {
      console.error(`Timer ${index + 1}: Failed to initialize:`, error);
      if (timerElement) {
        timerElement.removeAttribute('data-timer-initializing');
      }
    }
  });
}

// Simple, robust initialization function
function initTimersNow() {
  cleanupStaleTimers();
  initializeTimers();
}

// Initialize immediately if DOM is ready
function setupTimerInitialization() {
  console.log('=== Setting up timer initialization ===');
  
  // Try immediately
  console.log('Attempting immediate initialization...');
  initTimersNow();
  
  // Also try when DOM is ready (if not already)
  if (document.readyState === 'loading') {
    console.log('DOM still loading, waiting for DOMContentLoaded...');
    document.addEventListener("DOMContentLoaded", function() {
      console.log('DOMContentLoaded fired, initializing timers...');
      initTimersNow();
    });
  } else {
    console.log('DOM already loaded, initializing immediately');
  }
  
  // Multiple aggressive retry attempts for dynamically loaded content
  const retryDelays = [100, 300, 500, 1000, 2000, 3000, 5000, 8000, 10000];
  
  retryDelays.forEach((delay, index) => {
    setTimeout(function() {
      // Look for any timer elements that aren't initialized
      const uninitializedTimers = document.querySelectorAll(".announcement-timer-section__countdown:not([data-timer-initialized])");
      
      // Also check by data attribute
      const allTimersByAttr = document.querySelectorAll('[data-reset-hours]:not([data-timer-initialized])');
      
      if (uninitializedTimers.length > 0 || allTimersByAttr.length > 0) {
        console.log(`[Retry ${index + 1} @ ${delay}ms] Found ${uninitializedTimers.length} uninitialized timer(s) by class, ${allTimersByAttr.length} by attribute`);
        initTimersNow();
      }
      
      // Final comprehensive check on last retry
      if (index === retryDelays.length - 1) {
        console.log('=== FINAL TIMER CHECK ===');
        const allTimers = document.querySelectorAll(".announcement-timer-section__countdown, [data-reset-hours]");
        console.log(`Total timer elements found: ${allTimers.length}`);
        
        if (allTimers.length === 0) {
          console.warn('⚠️ NO TIMER ELEMENTS FOUND ON THIS PAGE');
          console.log('This may be because:');
          console.log('1. show_announcement is false AND design is not "design1"');
          console.log('2. enable_timer is false');
          console.log('3. Page handle is "venom-6"');
          console.log('4. Timer HTML hasn\'t loaded yet');
        }
        
        allTimers.forEach((timerEl, idx) => {
          const isInitialized = timerEl.hasAttribute('data-timer-initialized');
          const timer = activeTimers.get(timerEl);
          const hoursEl = timerEl.querySelector('[data-hours]');
          const minutesEl = timerEl.querySelector('[data-minutes]');
          const secondsEl = timerEl.querySelector('[data-seconds]');
          
          console.log(`Timer ${idx + 1}:`, {
            initialized: isInitialized,
            hasTimerInstance: !!timer,
            hasInterval: timer && !!timer.interval,
            hasHours: !!hoursEl,
            hasMinutes: !!minutesEl,
            hasSeconds: !!secondsEl,
            visible: timerEl.offsetParent !== null
          });
          
          if (!isInitialized && hoursEl && minutesEl && secondsEl) {
            console.log(`Timer ${idx + 1}: Not initialized but has all elements - initializing now`);
            initTimersNow();
          }
        });
      }
    }, delay);
  });
}

// Start initialization as soon as script loads
setupTimerInitialization();

// Watch for dynamically added timer elements using MutationObserver
if (typeof window.timerObserver === 'undefined') {
  window.timerObserver = null;
}

function setupTimerObserver() {
  // Clean up existing observer
  if (window.timerObserver) {
    window.timerObserver.disconnect();
    window.timerObserver = null;
  }
  
  window.timerObserver = new MutationObserver(function(mutations) {
    let shouldReinit = false;
    
    mutations.forEach(function(mutation) {
      // Check if any added nodes contain timer elements
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) { // Element node
          // Check if the node itself is a timer element
          if (node.classList && (
            node.classList.contains('announcement-timer-section__countdown') ||
            node.classList.contains('timer-section__countdown') ||
            node.classList.contains('cart-timer-section__countdown')
          )) {
            shouldReinit = true;
          }
          // Check if the node contains timer elements
          if (node.querySelector && (
            node.querySelector('.announcement-timer-section__countdown') ||
            node.querySelector('.timer-section__countdown') ||
            node.querySelector('.cart-timer-section__countdown')
          )) {
            shouldReinit = true;
          }
        }
      });
    });
    
    if (shouldReinit) {
      console.log('Timer elements detected in DOM changes - reinitializing...');
      setTimeout(() => {
        cleanupStaleTimers();
        initializeTimers();
      }, 100);
    }
  });
  
  // Start observing
  if (document.body) {
    window.timerObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    console.log('Timer MutationObserver set up to watch for dynamically added timer elements');
  }
}

// Set up observer after DOM is ready
if (document.body) {
  setupTimerObserver();
} else {
  document.addEventListener('DOMContentLoaded', setupTimerObserver);
}

// Handle Shopify page navigation (if theme uses AJAX navigation)
document.addEventListener('shopify:section:load', function(event) {
  console.log('Shopify section loaded, reinitializing timers...');
  cleanupStaleTimers();
  setTimeout(initializeTimers, 100);
});

// Handle Shopify section reload
document.addEventListener('shopify:section:reorder', function(event) {
  console.log('Shopify section reordered, reinitializing timers...');
  cleanupStaleTimers();
  setTimeout(initializeTimers, 100);
});

// Handle page visibility changes (when user switches tabs/windows)
document.addEventListener('visibilitychange', function() {
  if (!document.hidden) {
    // Page became visible - clean up and reinitialize if needed
    cleanupStaleTimers();
    initializeTimers();
  }
});

// Handle window focus (when navigating back to the tab)
window.addEventListener('focus', function() {
  cleanupStaleTimers();
  initializeTimers();
});

// Handle AJAX page navigation (common in Shopify themes)
window.addEventListener('popstate', function() {
  console.log('Page navigation detected, reinitializing timers...');
  cleanupStaleTimers();
  setTimeout(initializeTimers, 200);
});

// Watch for URL changes (for AJAX navigation)
let lastUrl = location.href;
if (typeof window.urlChangeObserver === 'undefined') {
  window.urlChangeObserver = new MutationObserver(() => {
    const url = location.href;
    if (url !== lastUrl) {
      lastUrl = url;
      console.log('URL changed, reinitializing timers...');
      cleanupStaleTimers();
      setTimeout(initializeTimers, 300);
    }
  });
  window.urlChangeObserver.observe(document, { subtree: true, childList: true });
}

// Handle timer reset globally
document.addEventListener('timerReset', function(event) {
  console.log('Timer reset to:', event.detail.newTargetDate);
  console.log('Reset duration:', event.detail.resetHours + 'h ' + event.detail.resetMinutes + 'm');
  
  // You can add custom behavior here when timer resets
  // For example: show a reset message, change styling, etc.
  const timerSection = event.target.closest('.timer-section, .cart-timer-section');
  if (timerSection) {
    // Optional: Add a brief visual indication that timer was reset
    timerSection.style.transition = 'all 0.3s ease';
    timerSection.style.transform = 'scale(1.05)';
    setTimeout(() => {
      timerSection.style.transform = 'scale(1)';
    }, 300);
  }
});

})(); // End IIFE - prevent duplicate execution
